import {
  CreateDattoAuthConfigInput,
  UpdateDattoAuthConfigInput,
  MappDattoCustomersInput,
  MapDattoDevicesInput,
} from "shared/types/integrations";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_INTEGRATION_BASE_URL;

export const createDattoAuthConfig = async (
  data: CreateDattoAuthConfigInput,
) => {
  try {
    const url = `${BASE_URL}/auth/datto`;
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateDattoAuthConfig = async (
  data: UpdateDattoAuthConfigInput,
) => {
  try {
    const url = `${BASE_URL}/auth/datto`;
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getDattoAuthConfig = async () => {
  try {
    const url = `${BASE_URL}/auth/datto`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Datto rejects a page size above 250 with a 400, so this is a hard ceiling and
 * not a tuning knob. Verified against UAT 2026-09-23: 250 OK, 251 rejected.
 */
export const DATTO_MAX_PAGE_SIZE = 250;

export const getDattoCustomers = async ({
  page = 0,
  perPage = DATTO_MAX_PAGE_SIZE,
}: {
  page?: number;
  perPage?: number;
}) => {
  try {
    const url = `${BASE_URL}/datto/sites?page=${page}&per_page=${perPage}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;

    if (resData?.success && resData?.statusCode === 200) {
      return {
        data: resData?.data?.sites || [],
        // Datto's own next-page URL, echoed by the integration service. Used only
        // as a has-more flag — it points at the vendor API host, so it cannot be
        // fetched directly. Never infer "last page" from the row count instead:
        // Datto silently caps `per_page`, so a short page is not a final page.
        hasMore: Boolean(resData?.data?.pageDetails?.nextPageUrl),
      };
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

/**
 * Pages by `page` / `per_page`, matching `/datto/sites`.
 *
 * This route was left on Level's `starting_after` cursor when `/datto/sites` was
 * migrated (`f7cb33a6f`), and Datto ignores that parameter outright — verified on
 * UAT 2026-09-23, where `starting_after` re-served page 0. It also read the
 * response as a bare array when the payload is `{ pageDetails, devices }`, so
 * `lastItemId` was always null and the list could never advance past page 1.
 */
export const getDattoDevices = async ({
  page = 0,
  perPage = DATTO_MAX_PAGE_SIZE,
}: {
  page?: number;
  perPage?: number;
}) => {
  try {
    const url = `${BASE_URL}/datto/devices?page=${page}&per_page=${perPage}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return {
        data: resData?.data?.devices || [],
        hasMore: Boolean(resData?.data?.pageDetails?.nextPageUrl),
      };
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

// need checking back payload
export const mapDattoCustomers = async (customers: MappDattoCustomersInput) => {
  try {
    const url = `${BASE_URL}/map/datto/customers`;
    const data = {
      customers,
    };
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getDattoMappedCustomers = async () => {
  try {
    const url = `${BASE_URL}/map/datto/customers`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

// need checking back payload
export const mapDattoDevices = async (data: MapDattoDevicesInput) => {
  try {
    const url = `${BASE_URL}/map/datto/devices`;
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getDattoMappedDevices = async () => {
  try {
    const url = `${BASE_URL}/map/datto/devices`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteDattoAuthConfig = async () => {
  try {
    const url = `${BASE_URL}/auth/datto`;
    const response = await axiosClient.delete(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const disconnectDattoIntegration = async () => {
  try {
    const url = `${BASE_URL}/disconnect/datto`;
    const response = await axiosClient.delete(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateDattoStatus = async (status: boolean) => {
  try {
    const url = `${BASE_URL}/v1/datto/msp/configuration/status/${status}`;
    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};
